import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { Store, CheckCircle2, Plus, Pause, Play, Trash2, Loader2, ArrowLeft, BadgeCheck, DollarSign, Package, Landmark, ImagePlus, Copy, Share2, Save, Eye, Users, MousePointerClick } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { ListingComposer } from "@/components/ListingComposer";
import { StockEditor } from "@/components/StockEditor";

const VERIF_LABEL = {
  pending: "Verification pending", verified_business: "Verified Business", verified_outfitter: "Verified Outfitter",
  verified_guide: "Verified Guide", verified_retailer: "Verified Retailer", unverified: "Unverified", suspended: "Suspended",
};
const dollars = (c) => `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 0 })}`;

function PlanCard({ id, plan, current, onSubscribe, busy }) {
  const featured = id === "pro";
  return (
    <div className={`p-6 border flex flex-col ${featured ? "border-blaze bg-forest text-bone" : "border-saddle/20 bg-white"}`} data-testid={`plan-${id}`}>
      <h3 className={`font-serif text-2xl ${featured ? "text-bone" : "text-forest"}`}>{plan.name}</h3>
      <div className="mt-2"><span className="font-serif text-4xl tabular-nums">{dollars(plan.price_cents)}</span><span className={featured ? "text-bone/60" : "text-charcoal/50"}>/mo</span></div>
      <p className={`text-xs mt-1 ${featured ? "text-blaze" : "text-saddle"}`}>{plan.fee_pct}% marketplace fee</p>
      <ul className="mt-4 space-y-1.5 flex-1">
        {plan.features.map((f, i) => <li key={i} className={`text-xs flex items-start gap-1.5 ${featured ? "text-bone/80" : "text-charcoal/70"}`}><CheckCircle2 size={13} className={featured ? "text-blaze mt-0.5 shrink-0" : "text-forest mt-0.5 shrink-0"} /> {f}</li>)}
      </ul>
      <button onClick={() => onSubscribe(id)} disabled={busy || current} className={`mt-5 py-2.5 text-sm font-semibold transition-colors disabled:opacity-60 ${featured ? "bg-blaze text-white hover:bg-blaze-dark" : "bg-forest text-bone hover:bg-forest-2"}`} data-testid={`subscribe-${id}`}>
        {current ? "Current plan" : busy ? <Loader2 size={15} className="animate-spin inline" /> : `Choose ${plan.name.replace("HomeRange ", "")}`}
      </button>
    </div>
  );
}

const CATS = ["Hunting Guide", "Outfitter", "Private Ranch", "Hunting Property", "Gear Company", "Apparel Brand", "Boot Company", "Optics Company", "Bowhunting Company", "Camping / Backcountry", "Accessories", "Calls & Decoys", "Taxidermist", "Meat Processor", "Dog Equipment", "Outdoor Retailer", "Other"];

export default function Business() {
  const navigate = useNavigate();
  const { isAuthed } = useAuth();
  const { openAuth } = useUI();
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", category: "Outfitter", description: "", location: "", website: "" });
  const [orders, setOrders] = useState(null);
  const [connect, setConnect] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [about, setAbout] = useState({ description: "", website: "", hours: "", location: "", socials: {}, accent: "" });
  const [coverPos, setCoverPos] = useState(50);
  const [analytics, setAnalytics] = useState(null);

  const load = useCallback(() => {
    api.myBusiness().then(setData).catch(() => {}).finally(() => setLoading(false));
    api.marketplacePlans().then(setPlans).catch(() => {});
  }, []);
  useEffect(() => { if (isAuthed) load(); else setLoading(false); }, [isAuthed, load]);
  useEffect(() => { if (data?.business?.subscription_status === "active") { api.myBusinessOrders().then(setOrders).catch(() => {}); api.connectStatus().then(setConnect).catch(() => {}); api.myBusinessAnalytics().then(setAnalytics).catch(() => {}); } }, [data]);

  useEffect(() => {
    const b = data?.business;
    if (b) {
      setAbout({ description: b.description || "", website: b.website || "", hours: b.hours || "", location: b.location || "", socials: b.socials || {}, accent: b.accent || "" });
      setCoverPos(parseInt(b.cover_pos || "50", 10) || 50);
    }
  }, [data]);

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try { await api.uploadBusinessCover(file); toast.success("Cover photo updated"); load(); }
    catch { toast.error("Cover upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try { await api.uploadBusinessLogo(file); toast.success("Logo updated"); load(); }
    catch { toast.error("Logo upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const saveAbout = async () => {
    const b = data?.business; if (!b) return;
    setBusy(true);
    try { await api.updateBusiness({ name: b.name, category: b.category, ...about }); toast.success("Storefront details saved"); load(); }
    catch { toast.error("Could not save details"); }
    finally { setBusy(false); }
  };

  const saveCoverPos = async (v) => {
    const b = data?.business; if (!b) return;
    try { await api.updateBusiness({ name: b.name, category: b.category, cover_pos: String(v) }); } catch { /* silent */ }
  };

  const shareLink = data?.business ? `${window.location.origin}/store/${data.business.id}` : "";
  const copyShare = () => { navigator.clipboard?.writeText(shareLink); toast.success("Storefront link copied"); };

  const viewsTrend = useMemo(() => {
    const byDay = analytics?.views_by_day || {};
    const out = [];
    for (let i = 29; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      out.push({ day: key.slice(5), views: byDay[key] || 0 });
    }
    return out;
  }, [analytics]);

  const startPayoutSetup = async () => {
    setConnecting(true);
    try {
      const res = await api.connectOnboard(window.location.origin);
      window.location.href = res.onboarding_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start payout setup");
      setConnecting(false);
    }
  };

  const biz = data?.business;

  const createBiz = async () => {
    if (!form.name.trim()) return toast.error("Business name required");
    setBusy(true);
    try { await api.createBusiness(form); toast.success("Business account created"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not create business"); }
    finally { setBusy(false); }
  };

  const subscribe = async (plan) => {
    setBusy(true);
    try {
      const res = await api.subscribe({ plan, origin_url: window.location.origin });
      window.location.href = res.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start checkout"); setBusy(false); }
  };

  const toggleStatus = async (l) => {
    const next = l.status === "active" ? "paused" : "active";
    await api.setListingStatus(l.id, next).catch(() => {});
    load();
  };
  const removeListing = async (l) => { await api.setListingStatus(l.id, "deleted").catch(() => {}); toast.success("Listing removed"); load(); };

  const refund = async (txnId) => {
    if (!window.confirm("Refund this order? This reverses the Stripe transfer and fee.")) return;
    try { await api.refundOrder(txnId); toast.success("Order refunded"); api.myBusinessOrders().then(setOrders); }
    catch (e) { toast.error(e?.response?.data?.detail || "Refund failed"); }
  };
  const shipOrder = async (t) => {
    const tn = window.prompt(`Add tracking for "${t.listing_title}" (leave blank to just mark shipped):`, "");
    if (tn === null) return;
    try { await api.markOrderShipped(t.id, { tracking_number: tn.trim() || null, carrier: null }); toast.success("Marked shipped — buyer notified"); api.myBusinessOrders().then(setOrders); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };
  const promote = async (l) => {
    try { const res = await api.promoteListing(l.id, window.location.origin); window.location.href = res.checkout_url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start promotion"); }
  };
  const pinListing = async (l) => {
    try { const res = await api.pinListing(l.id); toast.success(res.pinned_listing_id ? "Pinned to storefront" : "Unpinned"); load(); }
    catch { toast.error("Could not update pin"); }
  };

  if (!isAuthed) {
    return (
      <div className="min-h-screen bg-paper grid place-items-center px-6">
        <div className="text-center">
          <Store size={48} className="mx-auto text-forest" />
          <h1 className="font-serif text-3xl text-forest mt-4">HomeRange for Business</h1>
          <p className="text-charcoal/60 mt-2">Sign in to create your business account.</p>
          <button onClick={() => openAuth("register")} className="mt-5 bg-forest text-bone px-6 py-3 font-semibold" data-testid="business-signin">Sign in</button>
        </div>
      </div>
    );
  }
  if (loading) return <div className="min-h-screen bg-paper grid place-items-center"><Loader2 className="animate-spin text-forest" /></div>;

  return (
    <div className="min-h-screen bg-paper" data-testid="business-page">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
        <button onClick={() => navigate("/")} className="inline-flex items-center gap-1.5 text-sm text-saddle hover:text-forest mb-6" data-testid="business-back"><ArrowLeft size={15} /> Back to HomeRange</button>

        {/* No business yet → create */}
        {!biz && (
          <div className="max-w-lg" data-testid="create-business-form">
            <span className="hr-overline text-saddle">Get started</span>
            <h1 className="font-serif text-4xl text-forest mt-2">Create your business account</h1>
            <p className="text-charcoal/60 mt-2 text-sm">List guided hunts, gear and services to hunters across North America.</p>
            <div className="space-y-3 mt-6">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Business name" className="w-full border border-saddle/25 px-3 py-2.5 outline-none focus:border-forest" data-testid="biz-name" />
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border border-saddle/25 px-3 py-2.5 outline-none focus:border-forest" data-testid="biz-category">
                {CATS.map((c) => <option key={c}>{c}</option>)}
              </select>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Describe your business" rows={3} className="w-full border border-saddle/25 px-3 py-2.5 outline-none focus:border-forest" data-testid="biz-description" />
              <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Location (city, state)" className="w-full border border-saddle/25 px-3 py-2.5 outline-none focus:border-forest" data-testid="biz-location" />
              <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="Website (optional)" className="w-full border border-saddle/25 px-3 py-2.5 outline-none focus:border-forest" data-testid="biz-website" />
              <button onClick={createBiz} disabled={busy} className="bg-forest text-bone px-6 py-3 font-semibold w-full disabled:opacity-60" data-testid="create-business-btn">{busy ? <Loader2 size={16} className="animate-spin inline" /> : "Create business account"}</button>
            </div>
          </div>
        )}

        {/* Has business */}
        {biz && (
          <div>
            <div className="relative h-40 sm:h-52 bg-forest grain overflow-hidden border border-saddle/20" style={biz.cover ? { backgroundImage: `url(${biz.cover})`, backgroundSize: "cover", backgroundPosition: `center ${coverPos}%` } : {}} data-testid="business-cover">
              {!biz.cover && <div className="absolute inset-0 grid place-items-center text-bone/40 text-sm">No cover photo yet</div>}
              <div className="absolute bottom-3 left-4 w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white border-2 border-bone overflow-hidden grid place-items-center shadow-lg" data-testid="business-logo">
                {biz.logo ? <img src={biz.logo} alt="Logo" className="w-full h-full object-cover" /> : <Store size={22} className="text-saddle" />}
              </div>
              <div className="absolute bottom-3 right-3 flex gap-2">
                <label className="inline-flex items-center gap-1.5 bg-forest/80 backdrop-blur text-bone text-xs font-semibold px-3 py-2 cursor-pointer hover:bg-forest transition-colors" data-testid="logo-upload-label">
                  <ImagePlus size={14} /> Logo
                  <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} disabled={busy} data-testid="logo-upload-input" />
                </label>
                <label className="inline-flex items-center gap-1.5 bg-forest/80 backdrop-blur text-bone text-xs font-semibold px-3 py-2 cursor-pointer hover:bg-forest transition-colors" data-testid="cover-upload-label">
                  <ImagePlus size={14} /> {biz.cover ? "Change cover" : "Add cover"}
                  <input type="file" accept="image/*" className="hidden" onChange={uploadCover} disabled={busy} data-testid="cover-upload-input" />
                </label>
              </div>
            </div>
            {biz.cover ? (
              <div className="flex items-center gap-3 mt-2 mb-6" data-testid="cover-reposition">
                <span className="hr-overline text-saddle shrink-0">Reposition</span>
                <input type="range" min="0" max="100" value={coverPos} onChange={(e) => setCoverPos(+e.target.value)} onMouseUp={(e) => saveCoverPos(e.target.value)} onTouchEnd={() => saveCoverPos(coverPos)} className="flex-1 max-w-xs accent-blaze" data-testid="cover-pos-slider" />
              </div>
            ) : <div className="mb-6" />}
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="font-serif text-4xl text-forest">{biz.name}</h1>
                  {biz.verification_status?.startsWith("verified") && <span className="inline-flex items-center gap-1 bg-[#2f7d4f] text-white px-2 py-0.5 text-[11px] font-bold uppercase" data-testid="verif-badge"><BadgeCheck size={12} /> {VERIF_LABEL[biz.verification_status]}</span>}
                </div>
                <p className="text-charcoal/55 text-sm mt-1">{biz.category} · {biz.location || "—"} · {VERIF_LABEL[biz.verification_status]}</p>
              </div>
              {biz.plan && biz.subscription_status === "active" && <span className="bg-forest text-bone px-3 py-1.5 text-sm font-semibold" data-testid="current-plan">{plans[biz.plan]?.name}</span>}
            </div>

            {/* Inactive → show plans */}
            {biz.subscription_status !== "active" && (
              <div className="mt-8" data-testid="plans-section">
                <h2 className="font-serif text-2xl text-forest">Choose your plan to start listing</h2>
                <div className="grid sm:grid-cols-3 gap-4 mt-4">
                  {["business", "pro", "elite"].map((id) => plans[id] && <PlanCard key={id} id={id} plan={plans[id]} current={biz.plan === id} busy={busy} onSubscribe={subscribe} />)}
                </div>
              </div>
            )}

            {/* Active → dashboard */}
            {biz.subscription_status === "active" && (
              <div className="mt-8 space-y-8">
                {/* Storefront editor + share kit */}
                <div className="grid lg:grid-cols-[1.4fr_1fr] gap-4">
                  <div className="bg-white border border-saddle/20 p-5" data-testid="storefront-editor">
                    <h3 className="font-serif text-xl text-forest mb-3">Storefront details</h3>
                    <label className="hr-overline text-saddle">Description</label>
                    <textarea value={about.description} onChange={(e) => setAbout({ ...about, description: e.target.value })} rows={3} placeholder="Tell hunters about your business" className="w-full border border-saddle/25 px-3 py-2 text-sm outline-none focus:border-forest mt-1 mb-3" data-testid="about-description" />
                    <div className="grid sm:grid-cols-3 gap-3">
                      <div><label className="hr-overline text-saddle">Website</label><input value={about.website} onChange={(e) => setAbout({ ...about, website: e.target.value })} placeholder="https://" className="w-full border border-saddle/25 px-3 py-2 text-sm outline-none focus:border-forest mt-1" data-testid="about-website" /></div>
                      <div><label className="hr-overline text-saddle">Hours</label><input value={about.hours} onChange={(e) => setAbout({ ...about, hours: e.target.value })} placeholder="Mon–Sat 8–6" className="w-full border border-saddle/25 px-3 py-2 text-sm outline-none focus:border-forest mt-1" data-testid="about-hours" /></div>
                      <div><label className="hr-overline text-saddle">Location</label><input value={about.location} onChange={(e) => setAbout({ ...about, location: e.target.value })} placeholder="City, State" className="w-full border border-saddle/25 px-3 py-2 text-sm outline-none focus:border-forest mt-1" data-testid="about-location" /></div>
                    </div>
                    <label className="hr-overline text-saddle block mt-3">Social links</label>
                    <div className="grid sm:grid-cols-2 gap-3 mt-1">
                      {[["instagram", "Instagram URL"], ["youtube", "YouTube URL"], ["facebook", "Facebook URL"], ["x", "X / Twitter URL"]].map(([key, ph]) => (
                        <input key={key} value={about.socials?.[key] || ""} onChange={(e) => setAbout({ ...about, socials: { ...about.socials, [key]: e.target.value } })} placeholder={ph} className="w-full border border-saddle/25 px-3 py-2 text-sm outline-none focus:border-forest" data-testid={`about-social-${key}`} />
                      ))}
                    </div>
                    <label className="hr-overline text-saddle block mt-3">Storefront accent</label>
                    <div className="flex items-center gap-2 mt-1" data-testid="accent-picker">
                      {["#FF6A00", "#8B5E34", "#1F2A22", "#B23A48", "#2F6690", "#3A7D44", "#6B4E9E"].map((c) => (
                        <button key={c} type="button" onClick={() => setAbout({ ...about, accent: c })} className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${about.accent === c ? "border-forest ring-2 ring-forest/30" : "border-white shadow"}`} style={{ background: c }} aria-label={`Accent ${c}`} data-testid={`accent-${c.replace("#", "")}`} />
                      ))}
                    </div>
                    <div className="mt-3 border border-saddle/20 overflow-hidden select-none" data-testid="accent-preview">
                      <div className="h-14 bg-forest relative" style={biz.cover ? { backgroundImage: `url(${biz.cover})`, backgroundSize: "cover", backgroundPosition: `center ${coverPos}%` } : {}}>
                        <div className="absolute top-0 left-0 right-0 h-1" style={{ background: about.accent || "#FF6A00" }} />
                      </div>
                      <div className="bg-white px-3 py-2 flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-7 h-7 rounded-full bg-white border border-saddle/30 overflow-hidden grid place-items-center shrink-0 -mt-5 shadow">{biz.logo ? <img src={biz.logo} alt="" className="w-full h-full object-cover" /> : <Store size={12} className="text-saddle" />}</div>
                          <span className="font-serif text-sm text-forest truncate">{biz.name}</span>
                        </div>
                        <span className="text-white text-[10px] font-semibold px-2 py-1" style={{ background: about.accent || "#FF6A00" }}>Book</span>
                      </div>
                    </div>
                    <button onClick={saveAbout} disabled={busy} className="mt-4 inline-flex items-center gap-1.5 bg-forest text-bone px-5 py-2.5 text-sm font-semibold hover:bg-forest-2 transition-colors disabled:opacity-60" data-testid="save-about-btn"><Save size={15} /> Save details</button>
                  </div>
                  <div className="bg-forest text-bone p-5 grain relative" data-testid="share-kit">
                    <div className="relative z-10">
                      <h3 className="font-serif text-xl mb-1 flex items-center gap-2"><Share2 size={16} /> Share your storefront</h3>
                      <p className="text-bone/60 text-xs mb-3">Promote your HomeRange page anywhere.</p>
                      <div className="flex gap-2">
                        <input readOnly value={shareLink} className="flex-1 min-w-0 bg-forest-2 border border-bone/20 px-3 py-2 text-xs text-bone/80 outline-none" data-testid="share-link" />
                        <button onClick={copyShare} className="bg-blaze text-white px-3 py-2 text-sm font-semibold hover:bg-blaze-dark transition-colors shrink-0" data-testid="share-copy-btn"><Copy size={15} /></button>
                      </div>
                      <div className="flex items-center gap-3 mt-4">
                        <img src="/brand/badge.png" alt="HomeRange badge" className="w-12 h-12 object-contain" />
                        <a href="/brand/badge.png" download className="text-xs font-semibold text-blaze hover:underline" data-testid="share-badge-download">Download heritage badge</a>
                      </div>
                    </div>
                  </div>
                </div>
                {analytics && (
                  <div className="space-y-3" data-testid="analytics-summary">
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-white border border-saddle/20 p-4"><span className="hr-overline text-saddle flex items-center gap-1"><Eye size={12} /> Storefront views</span><p className="font-serif text-2xl text-forest mt-1 tabular-nums">{analytics.views}</p></div>
                      <div className="bg-white border border-saddle/20 p-4"><span className="hr-overline text-saddle flex items-center gap-1"><Users size={12} /> Followers</span><p className="font-serif text-2xl text-forest mt-1 tabular-nums">{analytics.followers}</p></div>
                      <div className="bg-white border border-saddle/20 p-4"><span className="hr-overline text-saddle flex items-center gap-1"><MousePointerClick size={12} /> Link clicks</span><p className="font-serif text-2xl text-forest mt-1 tabular-nums">{analytics.clicks}</p></div>
                    </div>
                    <div className="bg-white border border-saddle/20 p-4" data-testid="views-trend">
                      <span className="hr-overline text-saddle">Views · last 30 days</span>
                      <div className="mt-2" style={{ height: 70 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={viewsTrend} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
                            <XAxis dataKey="day" hide />
                            <Tooltip contentStyle={{ background: "#1F2A22", border: "1px solid #8B5E34", fontSize: 11, color: "#F3EFE6", padding: "4px 8px" }} labelStyle={{ color: "#F3EFE6" }} formatter={(v) => [`${v} views`, ""]} />
                            <Line type="monotone" dataKey="views" stroke="#FF6A00" strokeWidth={2} dot={false} isAnimationActive={false} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                )}
                {orders && (
                  <div className="grid grid-cols-3 gap-3" data-testid="revenue-summary">
                    <div className="bg-white border border-saddle/20 p-4"><span className="hr-overline text-saddle flex items-center gap-1"><DollarSign size={12} /> Gross</span><p className="font-serif text-2xl text-forest mt-1">{dollars(orders.revenue.gross)}</p></div>
                    <div className="bg-white border border-saddle/20 p-4"><span className="hr-overline text-saddle">HomeRange fees</span><p className="font-serif text-2xl text-forest mt-1">{dollars(orders.revenue.fees)}</p></div>
                    <div className="bg-white border border-saddle/20 p-4"><span className="hr-overline text-saddle">Your payout</span><p className="font-serif text-2xl text-[#2f7d4f] mt-1">{dollars(orders.revenue.payout)}</p></div>
                  </div>
                )}

                {orders && orders.orders.length > 0 && (
                  <div data-testid="payouts-ledger">
                    <h3 className="font-serif text-xl text-forest mb-3">Payouts ledger</h3>
                    <div className="overflow-x-auto bg-white border border-saddle/20">
                      <table className="w-full text-sm">
                        <thead><tr className="border-b border-saddle/15 text-left text-charcoal/50"><th className="p-2">Order</th><th className="p-2">Gross</th><th className="p-2">Fee</th><th className="p-2">Your cut</th><th className="p-2">Payout</th><th className="p-2"></th></tr></thead>
                        <tbody>
                          {orders.orders.map((t) => (
                            <tr key={t.id} className="border-b border-saddle/10" data-testid="ledger-row">
                              <td className="p-2">{t.listing_title}</td>
                              <td className="p-2 tabular-nums">{dollars(t.gross_cents)}</td>
                              <td className="p-2 tabular-nums text-saddle">-{dollars(t.fee_cents)}</td>
                              <td className="p-2 tabular-nums text-[#2f7d4f] font-semibold">{dollars(t.payout_cents)}</td>
                              <td className="p-2"><span className={`text-[11px] font-semibold px-2 py-0.5 ${t.order_status === "refunded" ? "bg-destructive/15 text-destructive" : t.order_status === "shipped" ? "bg-forest/15 text-forest" : t.payout_status === "paid_via_stripe" ? "bg-[#2f7d4f]/15 text-[#2f7d4f]" : "bg-saddle/15 text-saddle"}`}>{t.order_status === "refunded" ? "Refunded" : t.order_status === "shipped" ? "Shipped" : t.payout_status === "paid_via_stripe" ? "Paid to bank" : "Pending"}</span>{t.tracking_number && <span className="block text-[10px] text-charcoal/50 mt-0.5">#{t.tracking_number}</span>}</td>
                              <td className="p-2 whitespace-nowrap">
                                {t.order_status !== "refunded" && t.order_status !== "shipped" && t.listing_type === "product" && <button onClick={() => shipOrder(t)} className="text-[11px] font-semibold text-forest hover:underline mr-2" data-testid="ledger-ship">Mark shipped</button>}
                                {t.order_status !== "refunded" && <button onClick={() => refund(t.id)} className="text-[11px] font-semibold text-destructive hover:underline" data-testid="ledger-refund">Refund</button>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Payouts (Stripe Connect) */}
                <div className="bg-white border border-saddle/20 p-5" data-testid="payouts-card">
                  <h3 className="font-serif text-xl text-forest mb-1 flex items-center gap-2"><Landmark size={18} /> Payouts</h3>
                  {connect?.payouts_enabled ? (
                    <div className="flex items-center gap-2 text-sm text-[#2f7d4f]" data-testid="payouts-active"><CheckCircle2 size={16} /> Direct deposit active — sales are paid to your bank automatically (minus the {plans[biz.plan]?.fee_pct}% HomeRange fee).</div>
                  ) : connect?.connected ? (
                    <div>
                      <p className="text-sm text-charcoal/60">Your payout setup is incomplete. Finish it to receive funds directly to your bank.</p>
                      <button onClick={startPayoutSetup} disabled={connecting} className="mt-3 bg-forest text-bone px-5 py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="finish-payout-setup">{connecting ? <Loader2 size={15} className="animate-spin inline" /> : "Finish payout setup"}</button>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-charcoal/60">Until you set up direct deposit, your earnings are tracked as a pending balance. Connect your bank to get paid automatically on every sale.</p>
                      <button onClick={startPayoutSetup} disabled={connecting} className="mt-3 bg-forest text-bone px-5 py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="setup-payouts">{connecting ? <Loader2 size={15} className="animate-spin inline" /> : "Set up direct deposit"}</button>
                    </div>
                  )}
                </div>

                {/* Add listing */}
                <ListingComposer onPublished={load} />

                {/* Listings */}
                <div>
                  <h3 className="font-serif text-xl text-forest mb-3 flex items-center gap-2"><Package size={18} /> Your listings ({data.listings.filter((l) => l.status !== "deleted").length})</h3>
                  <div className="space-y-2" data-testid="listings-list">
                    {data.listings.filter((l) => l.status !== "deleted").map((l) => (
                      <motion.div key={l.id} layout className="bg-white border border-saddle/20 px-4 py-3" data-testid="listing-row">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 shrink-0 bg-bone/60 overflow-hidden grid place-items-center">
                            {l.images?.[0] ? <img src={l.images[0]} alt="" className="w-full h-full object-cover" /> : <Package size={16} className="text-saddle/40" />}
                          </div>
                          <span className="text-[10px] font-bold uppercase bg-saddle/15 text-saddle px-2 py-0.5">{l.listing_type}</span>
                          <div className="flex-1 min-w-0"><strong className="text-forest">{l.title}</strong><span className="block text-xs text-charcoal/50">${l.price} {l.images?.length ? `· ${l.images.length} photos` : ""} {l.species ? `· ${l.species}` : ""} {l.status === "paused" ? "· PAUSED" : ""} {l.featured_until && l.featured_until > new Date().toISOString() ? "· ⭐ FEATURED" : ""}</span></div>
                          <button onClick={() => pinListing(l)} className={`text-[11px] font-semibold border px-2 py-1 transition-colors ${biz.pinned_listing_id === l.id ? "bg-forest text-bone border-forest" : "text-forest hover:text-blaze border-forest/40"}`} data-testid="listing-pin">{biz.pinned_listing_id === l.id ? "Pinned" : "Pin"}</button>
                          <button onClick={() => promote(l)} className="text-[11px] font-semibold text-blaze hover:text-blaze-dark border border-blaze/40 px-2 py-1" data-testid="listing-feature">Feature</button>
                          <button onClick={() => toggleStatus(l)} className="text-saddle hover:text-forest" aria-label="Toggle" data-testid="listing-toggle">{l.status === "active" ? <Pause size={16} /> : <Play size={16} />}</button>
                          <button onClick={() => removeListing(l)} className="text-charcoal/40 hover:text-destructive" aria-label="Delete" data-testid="listing-delete"><Trash2 size={16} /></button>
                        </div>
                        {l.listing_type === "product" && <StockEditor listing={l} onUpdated={load} />}
                      </motion.div>
                    ))}
                    {data.listings.filter((l) => l.status !== "deleted").length === 0 && <p className="text-charcoal/50 text-sm">No listings yet — publish your first above.</p>}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
